import React from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiHorizontalRule,
  EuiIcon,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { OplMeeting } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { fmtLongDate, fmtTime } from "./format";

/**
 * The Meeting-Kopf: who is in the round, and the two house rules that govern how
 * it is worked through.
 *
 * The rules are printed rather than assumed. This is a shared list read by
 * people who are not in it every week, and "overdue first" plus the visibility
 * rule are exactly the two things a newcomer gets wrong.
 */

/** Where the summary send stands. `failed` is a warning, not an error. */
export type OplSendState = "idle" | "sending" | "sent" | "failed";

export interface MeetingHeadProps {
  meeting: OplMeeting | null;
  nextDate: string | null;
  sendState: OplSendState;
  /** Participants who actually have an address; the rest cannot be mailed. */
  recipientCount: number;
  onEdit: () => void;
  onSendSummary: () => void;
  /** opsanio/opl · update: edit the round and send its minutes. */
  canUpdate: boolean;
}

const i18n = "anketa.opsanio.opl";

export const MeetingHead = ({
  meeting,
  nextDate,
  sendState,
  recipientCount,
  onEdit,
  onSendSummary,
  canUpdate,
}: MeetingHeadProps): JSX.Element => {
  const { translate } = useAnketaI18n();
  const t = (k: string, d: string, values?: Record<string, unknown>) =>
    translate(`${i18n}.${k}`, { defaultMessage: d, values });

  if (!meeting) {
    return (
      <aside className="oplBoard__aside">
        <div className="oplEyebrow">{t("head", "Meeting-Kopf")}</div>
        <EuiSpacer size="s" />
        <EuiText size="s" color="subdued">
          {t("empty.body", "Die OPL beginnt mit der ersten Runde.")}
        </EuiText>
      </aside>
    );
  }

  const present = meeting.attendees.filter((a) => a.present);
  // Everyone not present is listed as absent - including guests. The concept
  // says a guest who is not there is not a guest of that round, but that is a
  // rule about how the list is *kept*, not a licence for the overview to drop
  // a row: a participant the user entered and can no longer see is worse than
  // an odd-looking one.
  const absent = meeting.attendees.filter((a) => !a.present);

  const row = (k: string, value: React.ReactNode) => (
    <>
      <span className="oplKV__k">{k}</span>
      <span>{value}</span>
    </>
  );

  return (
    <aside className="oplBoard__aside">
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem>
          <div className="oplEyebrow">{t("head", "Meeting-Kopf")}</div>
        </EuiFlexItem>
        {canUpdate && (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty size="xs" iconType="pencil" onClick={onEdit} flush="right">
              {t("edit", "Bearbeiten")}
            </EuiButtonEmpty>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      <EuiSpacer size="s" />

      <div className="oplKV">
        {row(t("head.id", "Meeting-ID"), <span className="oplMono">{meeting.key}</span>)}
        {row(t("head.date", "Datum"), fmtLongDate(meeting.date))}
        {row(t("head.start", "Start"), fmtTime(meeting.date))}
        {meeting.durationMinutes
          ? row(t("head.duration", "Dauer"), `${meeting.durationMinutes} min`)
          : null}
        {row(t("head.recorder", "Protokoll"), meeting.recorder ?? "-")}
        {row(
          t("head.moderator", "Moderator"),
          <>
            {meeting.moderator ?? "-"}
            {meeting.moderator && meeting.moderator === meeting.recorder && (
              <EuiText size="xs" color="subdued">
                {t("head.moderatorDefault", "Vorbelegung: Protokollführer")}
              </EuiText>
            )}
          </>,
        )}
        {row(t("head.next", "Nächster Termin"), nextDate ? fmtLongDate(nextDate) : "-")}
      </div>

      <EuiHorizontalRule margin="m" />

      <div className="oplEyebrow">{t("present", "Anwesend")}</div>
      <EuiSpacer size="xs" />
      {present.length === 0 && (
        <EuiText size="xs" color="subdued">
          -
        </EuiText>
      )}
      {present.map((a) => (
        <EuiFlexGroup
          key={a.email}
          gutterSize="xs"
          alignItems="baseline"
          responsive={false}
          wrap
        >
          <EuiFlexItem grow={false}>
            <EuiHealth color="success" textSize="xs">
              {a.name ?? a.email}
            </EuiHealth>
          </EuiFlexItem>
          {(a.fromTime || a.untilTime) && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="danger">
                {a.fromTime
                  ? t("from", "ab {{time}}", { time: a.fromTime })
                  : t("until", "bis {{time}}", { time: a.untilTime })}
              </EuiText>
            </EuiFlexItem>
          )}
          {a.isGuest && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {t("guest", "Gast")}
                {a.organisation ? ` · ${a.organisation}` : ""}
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      ))}

      <EuiSpacer size="m" />

      <div className="oplEyebrow">{t("absent", "Abwesend")}</div>
      <EuiSpacer size="xs" />
      {absent.length === 0 && (
        <EuiText size="xs" color="subdued">
          -
        </EuiText>
      )}
      {absent.map((a) => (
        <EuiFlexGroup
          key={a.email}
          gutterSize="xs"
          alignItems="baseline"
          responsive={false}
          wrap
        >
          <EuiFlexItem grow={false}>
            <EuiHealth color="danger" textSize="xs">
              {a.name ?? a.email}
            </EuiHealth>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              {a.absenceReason === "unexcused"
                ? t("absence.unexcused", "unentschuldigt")
                : t("absence.excused", "entschuldigt")}
            </EuiText>
          </EuiFlexItem>
          {a.substitute && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {t("substituteFor", "Vertretung: {{name}}", { name: a.substitute })}
              </EuiText>
            </EuiFlexItem>
          )}
          {a.isGuest && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                {t("guest", "Gast")}
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      ))}

      <EuiHorizontalRule margin="m" />

      <div className="oplEyebrow">
        {t("order", "Bearbeitungsreihenfolge")}
      </div>
      <EuiSpacer size="xs" />
      <EuiText size="xs" color="subdued">
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li>{t("order.1", "Überfällige Punkte")}</li>
          <li>{t("order.2", "Diese Woche fällig")}</li>
          <li>{t("order.3", "Neu eingegangen")}</li>
          <li>{t("order.4", "Übrige nur auf Zuruf")}</li>
        </ol>
      </EuiText>

      <EuiSpacer size="m" />

      <div
        className="oplNote"
        style={{
          padding: "9px 10px",
          background: "var(--opl-sunken)",
          borderLeft: "2px solid var(--opl-primary)",
        }}
      >
        <EuiIcon type="iInCircle" size="s" />{" "}
        {t(
          "visibilityRule",
          "Sichtbar, wenn der Status offen ist oder der Abschluss in der aktuellen Runde minus eins oder später liegt. Gezählt wird in Runden, nicht in Kalendertagen.",
        )}
      </div>

      <EuiHorizontalRule margin="m" />

      {/*
        Sending the minutes is part of closing a round, so it lives on the
        round's own surface. A send the relay refuses is reported as a warning
        and stays retryable - the round still took place.
      */}
      <div className="oplEyebrow">{t("notes", "Protokoll")}</div>
      <EuiSpacer size="xs" />
      <EuiText size="xs" color={meeting.summarySentAt ? "success" : "subdued"}>
        {meeting.summarySentAt
          ? t("summary.sent", "Versendet am {{date}}", {
              date: fmtLongDate(meeting.summarySentAt),
            })
          : t("summary.open", "Protokoll noch nicht versendet")}
      </EuiText>

      {sendState === "failed" && (
        <>
          <EuiSpacer size="xs" />
          <EuiText size="xs" color="warning">
            {t(
              "summary.failed",
              "Versand fehlgeschlagen. Die Runde gilt weiterhin als durchgeführt - der Versand kann wiederholt werden.",
            )}
          </EuiText>
        </>
      )}

      {canUpdate && (
      <>
      <EuiSpacer size="s" />
      <EuiButton
        size="s"
        fill={!meeting.summarySentAt}
        iconType="email"
        color={sendState === "failed" ? "warning" : "primary"}
        onClick={onSendSummary}
        isLoading={sendState === "sending"}
        disabled={recipientCount === 0 || sendState === "sending"}
      >
        {meeting.summarySentAt
          ? t("summary.resend", "Erneut senden")
          : t("summary.send", "Protokoll versenden")}
      </EuiButton>
      </>
      )}

      {recipientCount === 0 && (
        <>
          <EuiSpacer size="xs" />
          <EuiText size="xs" color="subdued">
            {t(
              "summary.noRecipients",
              "Kein Teilnehmer hat eine E-Mail-Adresse hinterlegt.",
            )}
          </EuiText>
        </>
      )}
    </aside>
  );
};
